import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Search, Download, Printer, AlertTriangle, Calendar, Building2, MapPin, AlertCircle, FileText, FileCode, PhoneCall } from 'lucide-react';
import { usePagamentiPendenti } from '../contexts/PagamentiPendentiContext';
import jsPDF from 'jspdf';

/** Badge compatto ✓/— per un avviso email (45gg o 20gg). */
function AvvisoBadge({ label, inviatoPer, inviatoAt, scadenza }) {
  const inviato = inviatoPer && inviatoPer === scadenza;
  return (
    <Badge
      variant="outline"
      className={`rounded-md text-[10px] font-mono px-1.5 ${inviato ? 'text-emerald-600 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30' : 'text-muted-foreground border-border'}`}
      title={inviato ? `Email ${label} inviata${inviatoAt ? ' il ' + new Date(inviatoAt).toLocaleDateString('it-IT') : ''}` : `Email ${label} non ancora inviata`}
    >
      {label} {inviato ? '✓' : '—'}
    </Badge>
  );
}

/** Cella "Avvisi inviati": badge 45gg/20gg + hint "da chiamare" se scaduto senza avvisi. */
function AvvisiCell({ imp }) {
  const avviso45 = imp.avviso_scadenza_inviato_per === imp.data_scadenza_verifica;
  const avviso20 = imp.sollecito_scadenza_inviato_per === imp.data_scadenza_verifica;
  const daChiamare = !avviso45 && !avviso20 && (imp.scaduto || imp.in_scadenza);
  return (
    <div className="flex flex-col gap-1 items-start" data-testid={`avvisi-${imp.id}`}>
      <div className="flex gap-1">
        <AvvisoBadge label="45gg" inviatoPer={imp.avviso_scadenza_inviato_per} inviatoAt={imp.avviso_scadenza_inviato_at} scadenza={imp.data_scadenza_verifica} />
        <AvvisoBadge label="20gg" inviatoPer={imp.sollecito_scadenza_inviato_per} inviatoAt={imp.sollecito_scadenza_inviato_at} scadenza={imp.data_scadenza_verifica} />
      </div>
      {daChiamare && (
        <span className="text-[10px] text-amber-600 font-medium flex items-center gap-1" title="Nessuna email automatica inviata per questa scadenza: contattare il cliente a voce">
          <PhoneCall className="h-3 w-3" /> da chiamare
        </span>
      )}
    </div>
  );
}
import autoTable from 'jspdf-autotable';
import { formatDate } from '../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificheScadenzaPage() {
  const navigate = useNavigate();
  const { hasPagamentoPendente } = usePagamentiPendenti();
  const [impianti, setImpianti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [puntiVendita, setPuntiVendita] = useState([]);
  const [zone, setZone] = useState([]);
  const [impostazioni, setImpostazioni] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroCitta, setFiltroCitta] = useState('');
  const [filtroZona, setFiltroZona] = useState('');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [filtroDataDa, setFiltroDataDa] = useState('');
  const [filtroDataA, setFiltroDataA] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [impiantiRes, clientiRes, puntiRes, zoneRes, impostazioniRes] = await Promise.all([
        axios.get(`${API}/impianti`),
        axios.get(`${API}/clienti`),
        axios.get(`${API}/punti-vendita`),
        axios.get(`${API}/zone`),
        axios.get(`${API}/impostazioni`)
      ]);
      setImpianti(impiantiRes.data);
      setClienti(clientiRes.data);
      setPuntiVendita(puntiRes.data);
      setZone(zoneRes.data);
      setImpostazioni(impostazioniRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const getClienteInfo = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente || {};
  };

  const getPuntoVenditaInfo = (puntoId) => {
    const punto = puntiVendita.find(p => p.id === puntoId);
    return punto || {};
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isExpiringSoon = (dateStr, days = 30) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = (date - today) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= days;
  };

  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  };

  // Estrai lista città uniche dai clienti
  const cittaUniche = [...new Set(clienti.map(c => c.citta_legale).filter(Boolean))].sort();

  // Filtra impianti con scadenza verifica
  const impiantiConScadenza = impianti
    .filter(imp => imp.data_scadenza_verifica)
    .map(imp => {
      const cliente = getClienteInfo(imp.cliente_id);
      const punto = getPuntoVenditaInfo(imp.punto_vendita_id);
      const days = getDaysUntilExpiry(imp.data_scadenza_verifica);
      return {
        ...imp,
        cliente_nome: cliente.ragione_sociale || '-',
        cliente_citta: cliente.citta_legale || '',
        cliente_telefono: cliente.telefono || '',
        punto_nome: punto.nome || '',
        punto_citta: punto.citta || '',
        giorni_alla_scadenza: days,
        scaduto: isExpired(imp.data_scadenza_verifica),
        in_scadenza: isExpiringSoon(imp.data_scadenza_verifica, 30)
      };
    })
    .sort((a, b) => new Date(a.data_scadenza_verifica) - new Date(b.data_scadenza_verifica));

  // Applica filtri
  const filteredImpianti = impiantiConScadenza.filter(imp => {
    const matchSearch = 
      imp.matricola?.toLowerCase().includes(search.toLowerCase()) ||
      imp.cliente_nome?.toLowerCase().includes(search.toLowerCase()) ||
      imp.tipo?.toLowerCase().includes(search.toLowerCase());
    
    const matchCitta = !filtroCitta || imp.cliente_citta === filtroCitta;
    
    const matchZona = !filtroZona || imp.zona === filtroZona;
    
    // Filtro date
    let matchDate = true;
    if (filtroDataDa) {
      matchDate = imp.data_scadenza_verifica >= filtroDataDa;
    }
    if (filtroDataA && matchDate) {
      matchDate = imp.data_scadenza_verifica <= filtroDataA;
    }
    
    let matchStato = true;
    if (filtroStato === 'scaduti') {
      matchStato = imp.scaduto;
    } else if (filtroStato === 'in_scadenza') {
      matchStato = imp.in_scadenza && !imp.scaduto;
    } else if (filtroStato === 'validi') {
      matchStato = !imp.scaduto && !imp.in_scadenza;
    }
    
    return matchSearch && matchCitta && matchZona && matchDate && matchStato;
  });

  // Statistiche
  const stats = {
    totale: impiantiConScadenza.length,
    scaduti: impiantiConScadenza.filter(i => i.scaduto).length,
    in_scadenza: impiantiConScadenza.filter(i => i.in_scadenza && !i.scaduto).length
  };

  // Export Excel
  const handleExport = async () => {
    try {
      // Prepara i dati per l'export
      const dataToExport = filteredImpianti.map(imp => ({
        matricola: imp.matricola,
        tipo: imp.tipo,
        marca: imp.marca || '',
        modello: imp.modello || '',
        cliente: imp.cliente_nome,
        citta: imp.cliente_citta,
        telefono: imp.cliente_telefono,
        punto_vendita: imp.punto_nome,
        data_scadenza: imp.data_scadenza_verifica,
        giorni_rimanenti: imp.giorni_alla_scadenza,
        stato: imp.scaduto ? 'SCADUTO' : imp.in_scadenza ? 'IN SCADENZA' : 'VALIDO'
      }));

      // Crea CSV
      const headers = ['Matricola', 'Tipo', 'Marca', 'Modello', 'Cliente', 'Città', 'Telefono', 'Punto Vendita', 'Scadenza Verifica Periodica', 'Giorni', 'Stato'];
      const csvContent = [
        headers.join(';'),
        ...dataToExport.map(row => [
          row.matricola,
          row.tipo,
          row.marca,
          row.modello,
          row.cliente,
          row.citta,
          row.telefono,
          row.punto_vendita,
          row.data_scadenza,
          row.giorni_rimanenti,
          row.stato
        ].join(';'))
      ].join('\n');

      // Download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `verifiche_scadenza_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };

  // Stampa
  const handlePrint = () => {
    window.print();
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header con logo
      let startY = 15;
      if (impostazioni?.logo) {
        try {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = impostazioni.logo;
          });
          doc.addImage(img, 'PNG', 14, 10, 40, 15);
          startY = 30;
        } catch (e) {
          // Logo non caricato, continua senza
          console.error('Logo non incluso nel PDF:', e);
        }
      }
      
      // Titolo
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('VERIFICHE PERIODICHE IN SCADENZA', pageWidth / 2, startY, { align: 'center' });
      
      // Filtri attivi
      startY += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const filtriAttivi = [];
      if (filtroZona) filtriAttivi.push(`Zona: ${filtroZona}`);
      if (filtroCitta) filtriAttivi.push(`Città: ${filtroCitta}`);
      if (filtroStato !== 'tutti') {
        const statoLabel = filtroStato === 'scaduti' ? 'Solo Scaduti' : 
                          filtroStato === 'in_scadenza' ? 'In Scadenza (30gg)' : 'Validi';
        filtriAttivi.push(`Stato: ${statoLabel}`);
      }
      if (filtroDataDa) filtriAttivi.push(`Da: ${filtroDataDa}`);
      if (filtroDataA) filtriAttivi.push(`A: ${filtroDataA}`);
      
      if (filtriAttivi.length > 0) {
        doc.setTextColor(100);
        doc.text(`Filtri: ${filtriAttivi.join(' | ')}`, 14, startY);
        startY += 6;
      }
      
      // Data generazione
      doc.text(`Generato il: ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`, 14, startY);
      doc.text(`Totale: ${filteredImpianti.length} verifiche`, pageWidth - 14, startY, { align: 'right' });
      startY += 8;
      
      // Tabella
      const tableData = filteredImpianti.map(imp => [
        imp.data_scadenza_verifica,
        imp.matricola,
        `${imp.tipo}${imp.marca ? ` (${imp.marca})` : ''}`,
        imp.cliente_nome,
        imp.cliente_citta || '-',
        imp.cliente_telefono || '-',
        imp.scaduto ? 'SCADUTO' : imp.in_scadenza ? 'IN SCADENZA' : 'VALIDO'
      ]);

      autoTable(doc, {
        startY: startY,
        head: [['Scadenza', 'Matricola', 'Tipo', 'Cliente', 'Città', 'Telefono', 'Stato']],
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [30, 58, 95], // Navy blue
          textColor: 255,
          fontStyle: 'bold',
        },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 35, fontStyle: 'bold' },
          2: { cellWidth: 45 },
          3: { cellWidth: 55 },
          4: { cellWidth: 35 },
          5: { cellWidth: 35 },
          6: { cellWidth: 28, halign: 'center' },
        },
        bodyStyles: {
          textColor: [50, 50, 50],
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 6) {
            const stato = data.cell.raw;
            if (stato === 'SCADUTO') {
              data.cell.styles.fillColor = [254, 202, 202]; // Rosso chiaro
              data.cell.styles.textColor = [153, 27, 27]; // Rosso scuro
              data.cell.styles.fontStyle = 'bold';
            } else if (stato === 'IN SCADENZA') {
              data.cell.styles.fillColor = [254, 243, 199]; // Giallo chiaro
              data.cell.styles.textColor = [146, 64, 14]; // Arancione scuro
              data.cell.styles.fontStyle = 'bold';
            } else {
              data.cell.styles.fillColor = [220, 252, 231]; // Verde chiaro
              data.cell.styles.textColor = [22, 101, 52]; // Verde scuro
            }
          }
        },
        margin: { left: 14, right: 14 },
      });

      // Footer con numero pagine
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Pagina ${i} di ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Salva
      const dataStr = new Date().toISOString().split('T')[0];
      doc.save(`verifiche_scadenza_${dataStr}.pdf`);
      toast.success('PDF generato con successo');
    } catch (error) {
      console.error('Errore generazione PDF:', error);
      toast.error('Errore durante la generazione del PDF');
    }
  };

  // Export XML
  const handleExportXML = () => {
    try {
      const dataToExport = filteredImpianti.map(imp => ({
        scadenza: imp.data_scadenza_verifica,
        matricola: imp.matricola,
        tipo: imp.tipo,
        marca: imp.marca || '',
        modello: imp.modello || '',
        cliente: imp.cliente_nome,
        citta: imp.cliente_citta || '',
        telefono: imp.cliente_telefono || '',
        zona: imp.zona || '',
        stato: imp.scaduto ? 'SCADUTO' : imp.in_scadenza ? 'IN_SCADENZA' : 'VALIDO',
        giorni_rimanenti: imp.giorni_alla_scadenza
      }));

      // Costruisci XML
      let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
      xml += '<verifiche_periodiche>\n';
      xml += `  <metadata>\n`;
      xml += `    <data_generazione>${new Date().toISOString()}</data_generazione>\n`;
      xml += `    <totale_record>${dataToExport.length}</totale_record>\n`;
      if (filtroZona) xml += `    <filtro_zona>${filtroZona}</filtro_zona>\n`;
      if (filtroCitta) xml += `    <filtro_citta>${filtroCitta}</filtro_citta>\n`;
      if (filtroStato !== 'tutti') xml += `    <filtro_stato>${filtroStato}</filtro_stato>\n`;
      if (filtroDataDa) xml += `    <filtro_data_da>${filtroDataDa}</filtro_data_da>\n`;
      if (filtroDataA) xml += `    <filtro_data_a>${filtroDataA}</filtro_data_a>\n`;
      xml += `  </metadata>\n`;
      xml += '  <impianti>\n';

      dataToExport.forEach(imp => {
        xml += '    <impianto>\n';
        xml += `      <scadenza_verifica>${imp.scadenza}</scadenza_verifica>\n`;
        xml += `      <matricola>${escapeXml(imp.matricola)}</matricola>\n`;
        xml += `      <tipo>${escapeXml(imp.tipo)}</tipo>\n`;
        xml += `      <marca>${escapeXml(imp.marca)}</marca>\n`;
        xml += `      <modello>${escapeXml(imp.modello)}</modello>\n`;
        xml += `      <cliente>${escapeXml(imp.cliente)}</cliente>\n`;
        xml += `      <citta>${escapeXml(imp.citta)}</citta>\n`;
        xml += `      <telefono>${escapeXml(imp.telefono)}</telefono>\n`;
        xml += `      <zona>${escapeXml(imp.zona)}</zona>\n`;
        xml += `      <stato>${imp.stato}</stato>\n`;
        xml += `      <giorni_rimanenti>${imp.giorni_rimanenti}</giorni_rimanenti>\n`;
        xml += '    </impianto>\n';
      });

      xml += '  </impianti>\n';
      xml += '</verifiche_periodiche>';

      // Download
      const blob = new Blob([xml], { type: 'application/xml;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `verifiche_scadenza_${new Date().toISOString().split('T')[0]}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('XML esportato con successo');
    } catch (error) {
      toast.error('Errore durante l\'export XML');
    }
  };

  // Helper per escape caratteri XML
  const escapeXml = (str) => {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="verifiche-scadenza-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-none">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
              <Calendar className="h-8 w-8" />
              Verifiche Periodiche
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Scadenze verifiche impianti</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-none" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 mr-2" />
            Esporta PDF
          </Button>
          <Button variant="outline" className="rounded-none" onClick={handleExportXML} data-testid="export-xml-btn">
            <FileCode className="h-4 w-4 mr-2" />
            Esporta XML
          </Button>
          <Button variant="outline" className="rounded-none" onClick={handleExport} data-testid="export-verifiche-btn">
            <Download className="h-4 w-4 mr-2" />
            Esporta CSV
          </Button>
          <Button variant="outline" className="rounded-none print:hidden" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Stampa
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:hidden">
        <Card className="rounded-none border-l-4 border-l-primary">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Totale Verifiche</p>
                <p className="text-3xl font-black">{stats.totale}</p>
              </div>
              <Calendar className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-none border-l-4 border-l-destructive">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Scadute</p>
                <p className="text-3xl font-black text-destructive">{stats.scaduti}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="rounded-none border-l-4 border-l-warning">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">In Scadenza (30gg)</p>
                <p className="text-3xl font-black text-warning">{stats.in_scadenza}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-warning" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 print:hidden">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per matricola, cliente, tipo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-none"
            data-testid="search-verifiche"
          />
        </div>
        
        <Select value={filtroCitta || "all"} onValueChange={(v) => setFiltroCitta(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-48 rounded-none" data-testid="filter-citta-verifiche">
            <SelectValue placeholder="Tutte le città" />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-60">
            <SelectItem value="all">Tutte le città</SelectItem>
            {cittaUniche.map((citta) => (
              <SelectItem key={citta} value={citta}>{citta}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filtroStato} onValueChange={setFiltroStato}>
          <SelectTrigger className="w-full md:w-48 rounded-none" data-testid="filter-stato-verifiche">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="tutti">Tutti</SelectItem>
            <SelectItem value="scaduti">Solo Scaduti</SelectItem>
            <SelectItem value="in_scadenza">In Scadenza (30gg)</SelectItem>
            <SelectItem value="validi">Validi</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filtroZona || "all"} onValueChange={(v) => setFiltroZona(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-48 rounded-none" data-testid="filter-zona-verifiche">
            <SelectValue placeholder="Tutte le zone" />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-60">
            <SelectItem value="all">Tutte le zone</SelectItem>
            {zone.map((z) => (
              <SelectItem key={z.id} value={z.nome}>{z.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Filtri Date */}
      <div className="flex flex-col md:flex-row gap-4 print:hidden">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono uppercase text-muted-foreground">Da:</span>
          <Input
            type="date"
            value={filtroDataDa}
            onChange={(e) => setFiltroDataDa(e.target.value)}
            className="rounded-none w-40"
            data-testid="filter-data-da"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono uppercase text-muted-foreground">A:</span>
          <Input
            type="date"
            value={filtroDataA}
            onChange={(e) => setFiltroDataA(e.target.value)}
            className="rounded-none w-40"
            data-testid="filter-data-a"
          />
        </div>
        {(filtroDataDa || filtroDataA || filtroZona || filtroCitta) && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { setFiltroDataDa(''); setFiltroDataA(''); setFiltroZona(''); setFiltroCitta(''); }}
            className="text-xs"
          >
            Pulisci filtri
          </Button>
        )}
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        {filteredImpianti.length} risultati trovati
      </p>

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th className="bg-amber-600/10">Scadenza Verifica</th>
              <th className="bg-primary/10">Matricola</th>
              <th className="bg-purple-600/10">Tipo</th>
              <th className="bg-blue-600/10">Cliente</th>
              <th className="hidden md:table-cell bg-emerald-600/10">Città</th>
              <th className="hidden lg:table-cell bg-cyan-600/10">Telefono</th>
              <th className="bg-sky-600/10">Avvisi inviati</th>
              <th className="bg-red-600/10">Stato</th>
            </tr>
          </thead>
          <tbody>
            {filteredImpianti.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted-foreground py-8">
                  Nessuna verifica trovata
                </td>
              </tr>
            ) : (
              filteredImpianti.map((imp) => (
                <tr 
                  key={imp.id} 
                  className="hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => navigate(`/impianti/${imp.id}`)}
                >
                  <td className="bg-amber-600/5 border-l-4 border-l-amber-600">
                    <div className="font-mono font-bold">
                      {formatDate(imp.data_scadenza_verifica)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {imp.giorni_alla_scadenza > 0 
                        ? `tra ${imp.giorni_alla_scadenza} giorni` 
                        : imp.giorni_alla_scadenza === 0 
                          ? 'OGGI' 
                          : `${Math.abs(imp.giorni_alla_scadenza)} giorni fa`}
                    </div>
                  </td>
                  <td className="font-mono font-bold bg-primary/5 border-l-4 border-l-primary">
                    {imp.matricola}
                  </td>
                  <td className="bg-purple-600/5 text-purple-600 dark:text-purple-400 border-l-4 border-l-purple-600">
                    {imp.tipo}
                    {imp.marca && <span className="text-xs text-muted-foreground ml-1">({imp.marca})</span>}
                  </td>
                  <td className="bg-blue-600/5 border-l-4 border-l-blue-600">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-blue-600 dark:text-blue-400">{imp.cliente_nome}</span>
                      {hasPagamentoPendente(imp.cliente_id) && (
                        <AlertCircle className="h-4 w-4 text-destructive" title="Fatture non pagate" />
                      )}
                    </div>
                    {imp.punto_nome && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {imp.punto_nome}
                      </div>
                    )}
                  </td>
                  <td className="hidden md:table-cell bg-emerald-600/5 text-emerald-600 dark:text-emerald-400 border-l-4 border-l-emerald-600">
                    {imp.cliente_citta || '-'}
                  </td>
                  <td className="hidden lg:table-cell bg-cyan-600/5 text-cyan-600 dark:text-cyan-400 border-l-4 border-l-cyan-600 font-mono">
                    {imp.cliente_telefono || '-'}
                  </td>
                  <td className="bg-sky-600/5 border-l-4 border-l-sky-600">
                    <AvvisiCell imp={imp} />
                  </td>
                  <td className="bg-red-600/5 border-l-4 border-l-red-600">
                    {imp.scaduto ? (
                      <Badge variant="destructive" className="rounded-none">
                        SCADUTO
                      </Badge>
                    ) : imp.in_scadenza ? (
                      <Badge className="rounded-none bg-warning text-warning-foreground">
                        IN SCADENZA
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-none text-success border-success">
                        VALIDO
                      </Badge>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [data-testid="verifiche-scadenza-page"], [data-testid="verifiche-scadenza-page"] * {
            visibility: visible;
          }
          [data-testid="verifiche-scadenza-page"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
